import { IconBell } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { NotificationDto } from "@repo/api/notification";
import { Button } from "./Button/Button";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import {
  notificationQueryKeys,
  useMarkAllNotificationsSeenMutation,
  useNotificationsQuery,
  useUnseenNotificationsQuery,
} from "../queries/notification-queries";
import LoadingSpinner from "./LoadingSpinner";
import { NotificationItem } from "./Notification";
import { cn } from "../utils";
import { queryClient } from "../queries/query-client";

const ESTIMATED_NOTIFICATION_ROW_HEIGHT = 72;

const NotificationsDropdown = ({ className }: { className?: string }) => {
  const [unseenIds, setUnseenIds] = useState<Set<string>>(new Set());
  const [isOpen, setIsOpen] = useState(false);
  const { data, isPending, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useNotificationsQuery({
      enabled: isOpen,
    });
  const { data: unseen = [] } = useUnseenNotificationsQuery();
  const { mutate: markAllNotificationsSeen } =
    useMarkAllNotificationsSeenMutation();
  const notifications = data?.pages.flatMap((page) => page.items) ?? [];
  const hasUnseen = unseen.length > 0;

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) {
          setUnseenIds(new Set(unseen.map((n) => n.externalId)));
          if (hasUnseen) {
            markAllNotificationsSeen();
          }
          queryClient.resetQueries({
            queryKey: notificationQueryKeys.listNotifications(),
          });
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="iconSm"
          className={cn("relative rounded-sm", className)}
        >
          <IconBell size={24} strokeWidth={2.5} className="text-[#404040]" />
          {hasUnseen && (
            <span
              className={cn(
                "absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full border border-white bg-linear-to-br from-primary-400 to-secondary-400",
              )}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex h-116 w-100 flex-col p-0"
        sideOffset={8}
      >
        {!isPending && notifications.length > 0 && (
          <div className="flex h-14 items-center justify-between border-b border-grey-200 px-6 py-2.5">
            <span className="text-sm font-semibold text-text-primary">
              Notifications
            </span>
          </div>
        )}
        {isPending ? (
          <div className="flex h-full flex-1 items-center justify-center px-4 py-24">
            <LoadingSpinner width={24} height={24} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex h-full flex-1 flex-col items-center justify-center gap-2 px-4 py-24 text-sm font-medium text-text-secondary">
            <IconBell size={28} strokeWidth={2} />
            You have no notifications.
          </div>
        ) : (
          <NotificationsVirtualList
            notifications={notifications}
            unseenIds={unseenIds}
            hasNextPage={hasNextPage ?? false}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};

function NotificationsVirtualList({
  notifications,
  unseenIds,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  notifications: NotificationDto[];
  unseenIds: Set<string>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const totalRows = notifications.length;

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? totalRows + 1 : totalRows,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ESTIMATED_NOTIFICATION_ROW_HEIGHT,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;

    if (lastItem.index >= totalRows - 1 && hasNextPage && !isFetchingNextPage) {
      onLoadMore();
    }
  }, [virtualItems, totalRows, hasNextPage, isFetchingNextPage, onLoadMore]);

  return (
    <div
      ref={scrollContainerRef}
      className="h-full min-h-0 flex-1 overflow-y-auto rounded-b-lg"
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualItems.map((virtualRow) => {
          const isLoaderRow = virtualRow.index >= totalRows;
          const notification = notifications[virtualRow.index];

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {isLoaderRow ? (
                <div className="flex justify-center py-4">
                  {isFetchingNextPage && (
                    <LoadingSpinner width={18} height={18} />
                  )}
                </div>
              ) : notification ? (
                <div className="border-b border-grey-200">
                  <NotificationItem
                    notification={notification}
                    isUnseen={unseenIds.has(notification.externalId)}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default NotificationsDropdown;
